import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Feather, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { http } from "@/lib/api";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import AuthorCard from "@/components/AuthorCard";
import WorkshopsCarousel from "@/components/WorkshopsCarousel";

// Inline shortcodes we recognise inside article Markdown body. Each shortcode
// must appear alone on its line (no surrounding text) so the split doesn't
// swallow legitimate content.
const SHORTCODES = {
    "<!-- CAROUSEL:workshops -->": WorkshopsCarousel,
};

function renderBodyWithShortcodes(content, mdComponents) {
    // Fast path: no shortcode present → single ReactMarkdown block
    const codes = Object.keys(SHORTCODES);
    if (!codes.some((c) => content.includes(c))) {
        return (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {content}
            </ReactMarkdown>
        );
    }
    // Split on each shortcode marker (on its own line) and inject the
    // corresponding React component between the Markdown chunks.
    const parts = [];
    let remaining = content;
    let key = 0;
    while (remaining.length) {
        const idx = codes
            .map((c) => ({ c, i: remaining.indexOf(c) }))
            .filter((x) => x.i !== -1)
            .sort((a, b) => a.i - b.i)[0];
        if (!idx) {
            parts.push(
                <ReactMarkdown key={key++} remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {remaining}
                </ReactMarkdown>
            );
            break;
        }
        const before = remaining.slice(0, idx.i);
        if (before.trim()) {
            parts.push(
                <ReactMarkdown key={key++} remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {before}
                </ReactMarkdown>
            );
        }
        const Comp = SHORTCODES[idx.c];
        parts.push(<Comp key={key++} />);
        remaining = remaining.slice(idx.i + idx.c.length);
    }
    return parts;
}

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return "";
    }
}

/**
 * Blog article page (`/journal/:slug`).
 *
 * Renders article body as **Markdown** via `react-markdown` — this is what
 * gives Google real H2/H3 tags, semantic lists, `<strong>` and true `<a>`
 * links from the article body (huge SEO win vs. plain `<p>` splits).
 *
 * The remark-gfm plugin adds tables, strikethrough, and autolinks so any
 * article we ever paste in behaves like modern Markdown.
 *
 * Custom `components` overrides preserve our design system (fonts, colors,
 * spacing) while letting the Markdown pipeline own the semantics. We also
 * force `target="_blank"` on external links only — internal `/…` links keep
 * SPA navigation.
 */
export default function BlogArticle() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        http.get(`/articles/${slug}`)
            .then(({ data }) => setArticle(data))
            .catch((e) => {
                if (e?.response?.status === 404) setNotFound(true);
            })
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Chargement…</div>;
    }
    // Slug alias 301-style redirect: if the article's canonical slug differs
    // from the URL we came in with (e.g. old newsletter link, alias set by
    // admin), we redirect the client to the canonical URL so Google/analytics
    // see a single URL per article. `replace` avoids polluting browser history.
    if (article && article.slug && article.slug !== slug) {
        return <Navigate to={`/journal/${article.slug}`} replace />;
    }
    if (notFound || !article) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
                <Seo title="Article introuvable · Foundation of Light" noindex path={`/journal/${slug}`} />
                <h1 className="font-serif text-3xl">Cet article n'existe plus</h1>
                <p className="text-muted-foreground">Il a peut-être été renommé ou retiré.</p>
                <Link to="/journal"><Button className="rounded-full">Retour au journal</Button></Link>
            </div>
        );
    }

    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        image: article.image_url,
        datePublished: article.created_at,
        author: {
            "@type": "Person",
            name: "Olivier Masurel",
            url: "https://foundation-of-light.com/histoire",
        },
        publisher: {
            "@type": "Organization",
            name: "Foundation of Light",
            logo: {
                "@type": "ImageObject",
                url: "https://foundation-of-light.com/brand/logo.png",
            },
        },
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://foundation-of-light.com/journal/${article.slug}`,
        },
    };

    // Markdown → React components with our design tokens. We keep the DOM
    // semantically correct (h2 stays h2, ul stays ul) — only styling changes.
    const mdComponents = {
        h1: ({ node, ...props }) => <h2 className="font-serif text-3xl md:text-4xl mt-10 mb-4" {...props} />,
        h2: ({ node, ...props }) => <h2 className="font-serif text-2xl md:text-3xl mt-10 mb-4 text-primary" {...props} />,
        h3: ({ node, ...props }) => <h3 className="font-serif text-xl md:text-2xl mt-8 mb-3" {...props} />,
        p: ({ node, ...props }) => <p className="mb-5 leading-relaxed" {...props} />,
        ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-5 space-y-2" {...props} />,
        ol: ({ node, ...props }) => <ol className="list-decimal pl-6 mb-5 space-y-2" {...props} />,
        li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
        strong: ({ node, ...props }) => <strong className="text-foreground font-semibold" {...props} />,
        em: ({ node, ...props }) => <em className="text-foreground/80" {...props} />,
        blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-2 border-primary/50 pl-4 italic text-foreground/70 my-6" {...props} />
        ),
        hr: () => <hr className="my-10 border-border/40" />,
        a: ({ node, href, ...props }) => {
            const isInternal = href && (href.startsWith("/") || href.startsWith("https://foundation-of-light.com"));
            if (isInternal) {
                // Rewrite absolute foundation-of-light.com URLs to internal SPA paths
                let to = href;
                if (href.startsWith("https://foundation-of-light.com")) {
                    to = href.replace("https://foundation-of-light.com", "") || "/";
                }
                return <Link to={to} className="text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary" {...props} />;
            }
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary"
                    {...props}
                />
            );
        },
    };

    return (
        <div className="min-h-screen" data-testid="blog-article-page">
            <Seo
                title={`${article.title} · Foundation of Light`}
                description={article.excerpt}
                path={`/journal/${article.slug}`}
                image={article.image_url}
                jsonLd={articleSchema}
            />

            {/* Hero image */}
            {article.image_url && (
                <div className="relative w-full h-[45vh] md:h-[60vh] overflow-hidden">
                    <img src={article.image_url} alt={article.image_alt || article.title} title={article.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                </div>
            )}

            <article className="max-w-3xl mx-auto px-6 -mt-24 relative pb-32">
                <div className="rounded-3xl bg-card border border-border/60 p-8 md:p-12 shadow-lg">
                    <Link to="/journal" className="inline-flex items-center gap-2 text-sm text-primary mb-6 hover:underline" data-testid="blog-article-back">
                        <ArrowLeft className="w-4 h-4" /> Tous les articles
                    </Link>
                    <div className="text-xs tracking-[0.25em] uppercase text-primary mb-4 font-medium flex items-center gap-2">
                        <Feather className="w-3.5 h-3.5" /> {formatDate(article.created_at)}
                    </div>
                    <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-6">{article.title}</h1>
                    <p className="text-lg text-foreground/75 leading-relaxed mb-10 border-l-2 border-primary/40 pl-4 italic">
                        {article.excerpt}
                    </p>

                    <div className="prose prose-lg max-w-none text-foreground/85" data-testid="blog-article-body">
                        {renderBodyWithShortcodes(article.content || "", mdComponents)}
                    </div>
                </div>

                {/* Author bio — E-E-A-T signal (photo, bio, /histoire link, social) */}
                <AuthorCard />

                {/* Post-CTA: invite to subscribe */}
                <div className="mt-12 rounded-3xl bg-primary/10 border border-primary/20 p-8 text-center" data-testid="blog-article-cta">
                    <Sparkles className="w-8 h-8 text-primary mx-auto mb-3" />
                    <h3 className="font-serif text-2xl mb-2">Envie de vivre cette lumière ensemble ?</h3>
                    <p className="text-sm text-foreground/70 mb-6 max-w-xl mx-auto">
                        Rejoignez un atelier de langage de lumière ou explorez nos abonnements pour un accompagnement régulier.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Link to="/evenements">
                            <Button className="rounded-full bg-primary text-primary-foreground">Voir les ateliers</Button>
                        </Link>
                        <Link to="/abonnements">
                            <Button variant="outline" className="rounded-full">Découvrir les abonnements</Button>
                        </Link>
                    </div>
                </div>
            </article>
        </div>
    );
}
